import type { BackendAllErrorCodes } from '@freelancer/datastore';
import type { ToastAlertService } from '@freelancer/ui/toast-alert';
import { ToastAlertColor, ToastAlertType } from '@freelancer/ui/toast-alert';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { PhoneRequestContext } from './phone-verification.types';

export function openErrorToast(
  toastAlertService: ToastAlertService,
  errorCode: BackendAllErrorCodes,
  context: PhoneRequestContext,
): void {
  switch (errorCode) {
    case ErrorCodeApi.PHONE_CANNOT_USE_NUMBER:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`This number cannot be used for verification.`,
      });
      break;
    case ErrorCodeApi.PHONE_COMPLETE_ID_VERIFICATION_REQUIRED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`You need to complete ID verification first.`,
      });
      break;
    case ErrorCodeApi.PHONE_INVALID_VERIFICATION:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Invalid verification request.`,
      });
      break;
    case ErrorCodeApi.PHONE_INVALID_VERIFICATION_CODE:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Verification code is invalid.`,
      });
      break;
    case ErrorCodeApi.EMAIL_VERIFICATION_REQUIRED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`You need to email verify first.`,
      });
      break;
    case ErrorCodeApi.PHONE_INVALID_NUMBER:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Invalid phone number.`,
      });
      break;
    case ErrorCodeApi.PHONE_MAXIMUM_NUMBER_OF_ATTEMPTS_REACHED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`You have reached the maximum allowable daily attempts.`,
      });
      break;
    case ErrorCodeApi.PHONE_NUMBER_ALREADY_USED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Phone number is already in use.`,
      });
      break;
    case ErrorCodeApi.PHONE_SAME_NUMBER:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Phone number is the same as the current phone number.`,
      });
      break;
    case ErrorCodeApi.TOO_MANY_REQUESTS:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`You have sent too many requests. Please try again later.`,
      });
      break;
    case ErrorCodeApi.USER_ACCOUNT_LIMITED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Your account is currently limited. Please verify your account details
      first before verifying your phone.`,
      });
      break;
    case ErrorCodeApi.WHATSAPP_CONSENT_REQUIRED:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`WhatsApp opt-in is required to verify this phone number.`,
      });
      break;
    case ErrorCodeApi.WHATSAPP_INVALID_NUMBER:
      toastAlertService.open({
        type: ToastAlertType.ERROR,
        color: ToastAlertColor.DARK,
        message: $localize`Please update your WhatsApp to continue.`,
      });
      break;
    default:
      if (context === PhoneRequestContext.SEND_CODE) {
        toastAlertService.open({
          type: ToastAlertType.ERROR,
          color: ToastAlertColor.DARK,
          message: $localize`Error sending the code. Please try again.`,
        });
      } else {
        toastAlertService.open({
          type: ToastAlertType.ERROR,
          color: ToastAlertColor.DARK,
          message: $localize`Error occured while verifying. Please try again.`,
        });
      }
      break;
  }
}

export function returnSendCodeErrorMessage(
  errorCode?: BackendAllErrorCodes,
): string {
  if (!errorCode) {
    return $localize`Error sending the code. Please try again.`;
  }

  switch (errorCode) {
    case ErrorCodeApi.PHONE_CANNOT_USE_NUMBER:
      return $localize`This number cannot be used for verification.`;
    case ErrorCodeApi.PHONE_COMPLETE_ID_VERIFICATION_REQUIRED:
      return $localize`You need to complete ID verification first.`;
    case ErrorCodeApi.PHONE_INVALID_VERIFICATION:
      return $localize`Invalid verification request.`;
    case ErrorCodeApi.PHONE_INVALID_VERIFICATION_CODE:
      return $localize`Verification code is invalid.`;
    case ErrorCodeApi.EMAIL_VERIFICATION_REQUIRED:
      return $localize`You need to email verify first.`;
    case ErrorCodeApi.PHONE_INVALID_NUMBER:
      return $localize`Invalid phone number.`;
    case ErrorCodeApi.PHONE_MAXIMUM_NUMBER_OF_ATTEMPTS_REACHED:
      return $localize`You have reached the maximum allowable daily attempts.`;
    case ErrorCodeApi.PHONE_NUMBER_ALREADY_USED:
      return $localize`Phone number is already in use.`;
    case ErrorCodeApi.PHONE_SAME_NUMBER:
      return $localize`Phone number is the same as the current phone number.`;
    case ErrorCodeApi.TOO_MANY_REQUESTS:
      return $localize`You have sent too many requests. Please try again later.`;
    case ErrorCodeApi.USER_ACCOUNT_LIMITED:
      return $localize`Your account is currently limited. Please verify your account details
      first before verifying your phone.`;
    case ErrorCodeApi.WHATSAPP_INVALID_NUMBER:
      return $localize`Please update your WhatsApp to continue.`;
    default:
      return $localize`Error sending the code. Please try again.`;
  }
}
